import { useState, useEffect, FormEvent, ReactNode } from "react";
import styles from "./MagicLoginGate.module.css";

interface Props {
    children: ReactNode;
    onAuthenticated?: () => void;
}

export const MagicLoginGate = ({ children, onAuthenticated }: Props) => {
    const [checking, setChecking] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [devVerifyUrl, setDevVerifyUrl] = useState("");
    const [error, setError] = useState("");

    // Check for token in URL (magic link callback)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        if (token) {
            verifyToken(token);
        } else {
            checkSession();
        }
    }, []);

    const checkSession = async () => {
        try {
            const resp = await fetch("/auth/session");
            if (resp.ok) {
                const data = await resp.json();
                if (data.authenticated) {
                    setAuthenticated(true);
                    onAuthenticated?.();
                }
            }
        } catch {
            // Not authenticated
        } finally {
            setChecking(false);
        }
    };

    const verifyToken = async (token: string) => {
        try {
            const resp = await fetch("/auth/verify", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token })
            });
            if (resp.ok) {
                // Clear token from URL
                window.history.replaceState({}, "", window.location.pathname + window.location.hash);
                setAuthenticated(true);
                onAuthenticated?.();
            } else {
                setError("This link has expired or already been used. Please request a new one.");
            }
        } catch {
            setError("Verification failed. Please try again.");
        } finally {
            setChecking(false);
        }
    };

    const handleSend = async (e: FormEvent) => {
        e.preventDefault();
        setError("");
        setSending(true);
        try {
            const resp = await fetch("/auth/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email })
            });
            if (resp.ok) {
                const data = await resp.json();
                if (data.dev_verify_url) {
                    setDevVerifyUrl(data.dev_verify_url);
                }
                setSent(true);
            } else {
                const data = await resp.json();
                setError(data.error || "Failed to send link. Please try again.");
            }
        } catch {
            setError("Network error. Please try again.");
        } finally {
            setSending(false);
        }
    };

    if (checking) {
        return null;
    }

    if (authenticated) {
        return <>{children}</>;
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.card}>
                <img src="/Gojeelogobrightgreen.svg" alt="Gojee" className={styles.logo} />
                <h2 className={styles.title}>Gojee AI Assistant</h2>
                <p className={styles.subtitle}>Enter your email to get started</p>

                {sent ? (
                    <div>
                        <div className={styles.sentIcon}>&#x2709;&#xFE0F;</div>
                        <p className={styles.sentMessage}>
                            {devVerifyUrl ? (
                                <>
                                    Dev mode — <a href={devVerifyUrl}>click here to sign in</a>
                                </>
                            ) : (
                                <>
                                    Check your email for a sign-in link.
                                    <br />
                                    <small>It may take a minute to arrive.</small>
                                </>
                            )}
                        </p>
                    </div>
                ) : (
                    <form className={styles.form} onSubmit={handleSend}>
                        <input
                            type="email"
                            className={styles.emailInput}
                            placeholder="you@company.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                            autoFocus
                        />
                        <button type="submit" className={`${styles.sendButton} ${sending ? styles.sending : ""}`} disabled={sending || !email}>
                            {sending ? (
                                <>
                                    <span className={styles.spinner} />
                                    Sending...
                                </>
                            ) : (
                                "Send me a sign-in link"
                            )}
                        </button>
                    </form>
                )}

                {error && <p className={styles.error}>{error}</p>}
            </div>
        </div>
    );
};
